import React from 'react';
import { MessageCircleIcon } from 'lucide-react';
import { CTAButton } from './CTAButton';
import { PriceBlock } from './PriceBlock';
import { PRODUCT_FACTS, PRODUCT_SUBTITLE } from './salesData';
import { HERO_IMAGE_URL } from '../utils/headMeta';

/**
 * Hero de venta: título, subtítulo, mockup del producto, precio tachado →
 * precio actual y CTA. Fondo negro con acento verde mensajería.
 *
 * En mobile el orden está comprimido a propósito (título → mockup chico →
 * precio + CTA) para que el botón de compra entre en la primera pantalla.
 * En desktop la columna izquierda apila título, compra y datos, y el mockup
 * ocupa la columna derecha completa.
 */
export function Hero() {
  return <section className="w-full bg-ink text-white" data-id="element-99" data-mp="ncq29gqxlsov">
      <div className="mx-auto flex w-full max-w-6xl flex-col px-5 pb-12 pt-6 sm:px-6 sm:pt-10 lg:grid lg:grid-cols-[0.95fr_1.05fr] lg:gap-x-14 lg:pb-20 lg:pt-16" data-id="element-100" data-mp="neaw145f97lo">
        {/* Título */}
        <div className="animate-hero-copy text-center lg:col-start-1 lg:row-start-1 lg:self-end lg:text-left" data-id="element-101" data-mp="380w0u35zb3fs">
          <span className="inline-flex items-center gap-2 rounded-full border border-graphite-line bg-graphite px-3 py-1 font-display text-[0.62rem] font-bold uppercase tracking-editorial text-wa sm:px-3.5 sm:py-1.5 sm:text-[0.66rem]" data-id="element-102" data-mp="3j6fylr125fed">
            <MessageCircleIcon className="h-3.5 w-3.5" aria-hidden strokeWidth={2.5} data-id="element-103" data-mp="3e963pvuwgbj5" />
            Producto digital
          </span>

          <h1 className="mt-3 font-display text-[1.85rem] font-extrabold leading-[1.05] sm:mt-5 sm:text-[2.9rem] lg:text-[3.35rem]" data-id="element-104" data-mp="30h9bzsx583na">
            100 Scripts de WhatsApp para{' '}
            <span className="mark-green" data-id="element-105" data-mp="2qkmthasahoof">cerrar más ventas</span>
          </h1>

          <p className="mx-auto mt-2.5 max-w-xl font-sans text-[0.95rem] leading-snug text-white/70 sm:mt-4 sm:text-[1.05rem] sm:leading-relaxed lg:mx-0" data-id="element-106" data-mp="2violil7p0yc1">
            {PRODUCT_SUBTITLE}
          </p>
        </div>

        {/* Mockup */}
        <div className="animate-hero-media mt-4 sm:mt-6 lg:col-start-2 lg:row-span-3 lg:row-start-1 lg:mt-0 lg:self-center" data-id="element-107" data-mp="2ljgyt7f2jrc9">
          <img src={HERO_IMAGE_URL} alt="Mockup de 100 Scripts de WhatsApp: la tapa del producto, celulares con conversaciones, fichas de scripts por etapa y el libro abierto en un script" width={1224} height={1280} fetchPriority="high" decoding="async" className="mx-auto h-[186px] w-auto rounded-2xl border border-graphite-line object-contain sm:h-[320px] lg:h-auto lg:w-full lg:rounded-3xl" data-id="element-108" data-mp="s44hnmgsjjhd" />
        </div>

        {/* Precio + CTA */}
        <div className="animate-hero-copy mt-4 rounded-2xl border border-graphite-line bg-graphite p-4 sm:mt-6 sm:p-6 lg:col-start-1 lg:row-start-2 lg:mt-8" data-id="element-109" data-mp="112g6biy3k776">
          <PriceBlock tone="dark" layout="inline" className="sm:hidden" data-id="element-110" data-mp="x0uy4awich3b" />
          <PriceBlock tone="dark" className="hidden sm:flex" data-id="element-111" data-mp="3lu07neppzt62" />
          <CTAButton className="mt-4 w-full sm:mt-5" data-id="element-112" data-mp="3hrlmgnykwhbm" />
          <p className="mt-2.5 text-center font-sans text-[0.72rem] text-white/50 sm:mt-3 sm:text-left sm:text-[0.75rem]" data-id="element-113" data-mp="3hpsgzbzpc357">
            Acceso inmediato después de la compra · 100% digital
          </p>
        </div>

        {/* Datos del producto */}
        <ul className="mt-5 flex flex-wrap justify-center gap-2 lg:col-start-1 lg:row-start-3 lg:mt-6 lg:justify-start" data-id="element-114" data-mp="269hdo8dhjb4v">
          {PRODUCT_FACTS.map(fact => <li key={fact} className="rounded-full border border-graphite-line bg-graphite px-3 py-1.5 font-display text-[0.68rem] font-bold uppercase tracking-[0.06em] text-white/75 sm:text-[0.7rem]" data-id="element-115" data-mp="2b2snmidlg810">
              {fact}
            </li>)}
        </ul>
      </div>
    </section>;
}